import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "What platforms does ClawdVoice support?",
    a: "ClawdVoice is available for macOS and Windows. Both platforms receive the same features and updates simultaneously.",
  },
  {
    q: "Which key do I press to activate it?",
    a: "You hold the FN key on your keyboard. While holding it, speak your command. Release to execute. That's all.",
  },
  {
    q: "Does it work offline?",
    a: "Basic app-opening commands work offline. Speech transcription and email drafting require an internet connection for processing.",
  },
  {
    q: "What apps does ClawdVoice support?",
    a: "ClawdVoice works with any installed application. It can open Slack, Teams, Gmail, any browser, Notion, VS Code, Spotify — if it's installed, you can open it by voice.",
  },
  {
    q: "Is my voice data stored?",
    a: "No. Voice data is processed in real-time and never stored or sent to third-party servers. Your privacy is a core design principle of ClawdVoice.",
  },
  {
    q: "Can I use ClawdVoice with any microphone?",
    a: "Yes. ClawdVoice works with built-in laptop microphones, external USB mics, and headsets. Any microphone your OS recognizes will work.",
  },
  {
    q: "Is there a free trial for Pro?",
    a: "The Free tier is available indefinitely with 50 commands/day. The Pro plan is available with a 14-day free trial — no credit card required.",
  },
];

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`faq-item-${index}`}
      className="border-b border-zinc-100 last:border-0"
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between py-6 text-left group"
      >
        <span
          style={{ fontFamily: "Manrope, sans-serif" }}
          className="text-base md:text-lg font-medium text-zinc-900 group-hover:text-zinc-600 transition-colors pr-8"
        >
          {item.q}
        </span>
        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
          {open ? (
            <Minus size={16} strokeWidth={2} className="text-zinc-400" />
          ) : (
            <Plus size={16} strokeWidth={2} className="text-zinc-400" />
          )}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-zinc-500 text-sm leading-relaxed max-w-2xl">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-32 bg-white border-t border-zinc-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left */}
          <div ref={titleRef}>
            <motion.p
              initial={{ opacity: 0 }}
              animate={titleInView ? { opacity: 1 } : {}}
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-4"
            >
              FAQ
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={titleInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              style={{ fontFamily: "Manrope, sans-serif" }}
              className="text-3xl md:text-4xl font-semibold tracking-tight text-zinc-900"
            >
              Common questions.
            </motion.h2>
          </div>

          {/* Right: Accordion */}
          <div className="lg:col-span-2">
            {faqs.map((item, i) => (
              <FAQItem key={i} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
